import React, { useState } from "react";
import ProductPageLayout from "@/components/ProductPageLayout";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import challengesHero from "@/assets/challenges-hero.webp";
import challengesLeaderboard from "@/assets/challenges-leaderboard.webp";
import challengesClaim from "@/assets/challenges-claim.png";
import { Trophy, Users, Target, TrendingUp, Star, ArrowRight, CheckCircle, Zap, Calendar, Award } from "lucide-react";
import { DemoBookingModal } from "@/components/DemoBookingModal";
import TrustLogosSection from "@/components/trust/TrustLogosSection";
import HeroBadgesSection from "@/components/hero/HeroBadgesSection";

const Challenges = () => {
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);

  const challengeTypes = [
    {
      icon: <Award className="h-6 w-6" />,
      title: "Recognition Challenges",
      description: "Drive peer recognition and appreciation across your organization",
      features: ["Recognition goals", "Team participation", "Appreciation targets", "Culture building"],
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: <Target className="h-6 w-6" />,
      title: "Learning & Development",
      description: "Boost completion rates for training and professional development",
      features: ["Course completion", "Skill building", "Certification tracking", "Knowledge sharing"],
      color: "from-purple-500 to-pink-500"
    },
    {
      icon: <Users className="h-6 w-6" />,
      title: "Wellness Challenges",
      description: "Promote healthy habits and work-life balance initiatives",
      features: ["Health goals", "Team activities", "Habit tracking", "Wellness rewards"],
      color: "from-green-500 to-teal-500"
    },
    {
      icon: <Zap className="h-6 w-6" />,
      title: "Compliance & Security",
      description: "Ensure completion of critical compliance training and initiatives",
      features: ["Training completion", "Policy acknowledgment", "Security awareness", "Deadline tracking"],
      color: "from-orange-500 to-amber-500"
    },
    {
      icon: <Trophy className="h-6 w-6" />,
      title: "Team Competitions",
      description: "Foster collaboration through friendly team-based competitions",
      features: ["Team leaderboards", "Cross-department", "Point accumulation", "Team bonding"],
      color: "from-pink-500 to-rose-500"
    },
    {
      icon: <Calendar className="h-6 w-6" />,
      title: "Custom Initiatives",
      description: "Create challenges for any organizational goal or value",
      features: ["Flexible goals", "Custom metrics", "Time-bound", "Tailored rewards"],
      color: "from-emerald-500 to-teal-500"
    }
  ];

  const benefits = [
    {
      icon: <TrendingUp className="h-6 w-6" />,
      title: "85% Higher Participation",
      description: "Gamification drives significantly higher engagement in strategic initiatives"
    },
    {
      icon: <Users className="h-6 w-6" />,
      title: "Stronger Team Bonds",
      description: "Shared challenges create collaboration and camaraderie across teams"
    },
    {
      icon: <Target className="h-6 w-6" />,
      title: "Measurable Results",
      description: "Track completion rates and outcomes to prove ROI of engagement initiatives"
    }
  ];

  return (
    <ProductPageLayout
      title="Team Challenges - Gamified Employee Engagement Platform"
      description="Assembly's Team Challenges feature helps organizations create engaging competitions that drive strategic behaviors, foster team connections, and gamify important initiatives through recognition and rewards."
      keywords="employee engagement challenges, team competition software, workplace challenges, gamification platform, team engagement activities, employee competition tools, wellness challenges, recognition challenges"
      ctaTitle="Want to Learn More?"
      ctaDescription="See how Assembly's Team Challenges can transform your employee engagement initiatives."
      ctaButtonText="Get a Demo"
      ctaVariant="blue"
      onCtaClick={() => setIsDemoModalOpen(true)}
    >
      {/* Full Height Hero Section */}
      <div className="relative bg-gradient-to-br from-slate-50 via-gray-50 to-coral-50/30 font-sans overflow-hidden min-h-screen">
        {/* Subtle Background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-gradient-to-br from-coral-500/8 via-orange-500/5 to-coral-400/4 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute top-1/3 -left-40 w-[500px] h-[500px] bg-gradient-to-br from-orange-400/6 via-coral-500/5 to-slate-400/3 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>
        
        {/* Dot Grid Pattern */}
        <div className="absolute inset-0 opacity-[0.15]">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, rgba(251, 113, 133, 0.3) 2px, transparent 0)`,
            backgroundSize: '48px 48px'
          }}></div>
        </div>

        <section className="relative flex items-center justify-center py-8 lg:py-10 px-4 lg:px-8 min-h-screen pt-20 lg:pt-24">
          <div className="container mx-auto px-4 2xl:px-12 max-w-none lg:max-w-7xl xl:max-w-[1400px] 2xl:max-w-[1600px] relative px-6 lg:px-6 xl:px-8 pb-3 lg:pb-4">
            <div className="flex flex-col lg:grid lg:grid-cols-7 gap-6 lg:gap-4 items-start">
              {/* Left Section: Text Content */}
              <div className="flex flex-col items-start text-left lg:col-span-4 h-full justify-center space-y-4 lg:space-y-6">
                {/* Pills */}
                <motion.div
                  className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-2 sm:gap-3 mt-6 lg:mt-8 mb-3 lg:mb-4"
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7 }}
                >
                  <div className="relative overflow-hidden px-3 py-1.5 rounded-full bg-black/5 border border-black/10 backdrop-blur-sm hover:bg-black/10 transition-all duration-300 hover:-translate-y-0.5">
                    <h1 className="text-xs font-normal text-gray-900 tracking-wide uppercase">Drive Results Through Friendly Competition</h1>
                  </div>
                </motion.div>

                {/* Main Heading */}
                <div className="mb-4 lg:mb-6">
                  <motion.h2
                    className="text-4xl sm:text-5xl md:text-6xl lg:text-5xl xl:text-6xl font-bold text-gray-900 tracking-tight font-sans"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                  >
                    <span className="block mb-0 sm:mb-0 md:mb-0 lg:mb-4">Transform initiatives into</span>
                    <span className="block">
                      <span className="mr-2">engaging</span>
                      <span className="relative inline-block font-extrabold">
                        challenges
                        <span className="absolute inset-x-0 bottom-1 h-3 bg-gradient-to-r from-coral-200/60 via-coral-300/70 to-coral-200/60 rounded-sm transform scale-x-0 origin-left transition-transform duration-1000 ease-out -z-10" style={{
                          transform: 'scaleX(1)',
                          transitionDelay: '200ms'
                        }}></span>
                      </span>
                    </span>
                  </motion.h2>
                </div>

                {/* Subheading */}
                <motion.p
                  className="text-sm sm:text-base lg:text-base xl:text-lg text-gray-600 mb-6 lg:mb-8 leading-relaxed"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                >
                  Assembly Challenges turns strategic goals into fun, engaging competitions. Drive recognition, boost learning completion, promote wellness, and achieve organizational objectives through gamification that motivates participation and delivers measurable results.
                </motion.p>
                
                {/* Action Buttons */}
                <motion.div
                  className="mb-4 lg:mb-6 w-full flex flex-col sm:flex-row gap-3"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.6 }}
                >
                  <Button 
                    size="lg" 
                    className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white shadow-sm hover:shadow-md transition-all duration-200" 
                    onClick={() => setIsDemoModalOpen(true)}
                  >
                    Book a Demo
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </motion.div>
                
                {/* Trust Ratings */}
                <motion.div
                  className="flex flex-col sm:flex-row gap-3 sm:gap-6 items-start sm:items-center w-full"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.8 }}
                >
                  <div className="flex items-center gap-2">
                    <Star className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-400 fill-yellow-400" />
                    <span className="text-sm font-semibold text-gray-700">4.9 / 5 on G2</span>
                    <img src="/lovable-uploads/e68d106f-69a9-45e8-8624-7f09b1ae1e6f.png" alt="G2 Logo" className="h-4 w-4 sm:h-5 sm:w-5" width={20} height={20} />
                  </div>
                  <a href="https://www.g2.com/products/assembly-2021-12-09/reviews" target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
                    <span className="text-sm font-semibold text-gray-500">3000+ reviews</span>
                  </a>
                </motion.div>
              </div>
              
              {/* Right Section: Hero Image */}
              <div className="relative w-full flex items-center justify-center lg:col-span-3 mt-6 lg:mt-0">
                <div className="relative group">
                  <motion.img
                    src={challengesLeaderboard}
                    alt="Team Challenges showing security training, company 5k run, and open enrollment challenges with rewards"
                    className="w-full h-auto object-cover rounded-xl transition-transform duration-300 group-hover:scale-105"
                    loading="eager"
                    decoding="async"
                    fetchPriority="high"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                  />
                </div>
              </div>
            </div>
            
            {/* Trust Section at Bottom */}
            <motion.div
              className="text-center pt-8 lg:pt-12 mt-8 space-y-8"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.0 }}
            >
              <p className="text-lg text-gray-600">
                The #1 Employee Recognition & Rewards Platform - Trusted by Leading Teams Worldwide
              </p>
              <HeroBadgesSection />
              <div className="pt-4">
                <TrustLogosSection />
              </div>
            </motion.div>
          </div>
        </section>
      </div>

      {/* Real-Time Tracking Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                Manage multiple challenges simultaneously
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Run multiple challenges at once across different teams and departments. Assembly's platform makes it easy to manage everything from a central dashboard with automated reminders, scheduled point releases, and real-time progress tracking.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-coral-500 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-sm lg:text-base font-semibold text-gray-900 mb-1">Automated Reminders</h3>
                    <p className="text-gray-600">Send scheduled reminders to keep teams engaged throughout the challenge</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-coral-500 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-sm lg:text-base font-semibold text-gray-900 mb-1">Point Release Management</h3>
                    <p className="text-gray-600">Distribute points automatically upon completion or manually for approval-based challenges</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-coral-500 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-sm lg:text-base font-semibold text-gray-900 mb-1">Multi-Team Coordination</h3>
                    <p className="text-gray-600">Run department-specific and company-wide challenges simultaneously</p>
                  </div>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <img
                src={challengesHero}
                alt="Challenge management dashboard showing multiple active team challenges"
                className="w-full rounded-3xl shadow-xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Easy Claim Process Section */}
      <section className="py-20 bg-gradient-to-br from-orange-50 to-amber-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-2 lg:order-1"
            >
              <img
                src={challengesClaim}
                alt="Challenge claim interface showing 10K Steps Challenge with submission form"
                className="w-full rounded-3xl shadow-2xl"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-1 lg:order-2"
            >
              <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
                <Trophy className="h-4 w-4" />
                Simple Submission
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                Easy claim process with optional approval
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Employees can easily claim challenge completion with a simple submission form. Add descriptions, attach proof, and submit for automatic or manual approval based on your challenge settings.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-coral-500 rounded-full"></div>
                  <span className="text-gray-700">One-click claim submissions with optional attachments</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-coral-500 rounded-full"></div>
                  <span className="text-gray-700">Flexible approval workflows for verification</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-coral-500 rounded-full"></div>
                  <span className="text-gray-700">Automatic reward distribution upon approval</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Challenge Types Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Challenges for Every Goal
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              From wellness programs to compliance training, create custom challenges that drive participation and achieve your organizational objectives.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {challengeTypes.map((type, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow"
              >
                <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${type.color} flex items-center justify-center text-white mb-4`}>
                  {type.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{type.title}</h3>
                <p className="text-gray-600 mb-4">{type.description}</p>
                <ul className="space-y-2">
                  {type.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-600">
                      <CheckCircle className="h-4 w-4 text-coral-500 mr-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              How Team Challenges Work
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Create, launch, and manage engaging challenges in minutes with Assembly's intuitive platform.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white text-2xl font-bold mx-auto mb-6">
                1
              </div>
              <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-4">Design Your Challenge</h3>
              <p className="text-gray-600 leading-relaxed">
                Choose challenge type, set goals and timelines, define rewards, and customize rules to align with your objectives.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-center"
            >
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-2xl font-bold mx-auto mb-6">
                2
              </div>
              <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-4">Track Progress</h3>
              <p className="text-gray-600 leading-relaxed">
                Monitor participation with real-time leaderboards, automated updates, and progress tracking that keeps teams motivated.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-center"
            >
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-500 to-teal-500 flex items-center justify-center text-white text-2xl font-bold mx-auto mb-6">
                3
              </div>
              <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-4">Celebrate Success</h3>
              <p className="text-gray-600 leading-relaxed">
                Reward participants with points, recognition, and prizes. Analyze results to optimize future challenges.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Why Teams Love Challenges
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 rounded-full bg-coral-100 flex items-center justify-center text-coral-600 mx-auto mb-6">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <DemoBookingModal 
        open={isDemoModalOpen} 
        onOpenChange={setIsDemoModalOpen} 
      />
    </ProductPageLayout>
  );
};

export default Challenges;
